"use client";

import { useEffect, useState } from "react";
import { useToastStore, type Toast } from "@/stores/toast-store";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/use-translations";
import { getTranslation } from "@/lib/i18n/translations";
import type { Locale } from "@/i18n/config";

/** Resolve translation keys to user-facing text (e.g. when a key slips through) */
function resolveToastMessage(message: string, locale: Locale): string {
  const knownKeys = [
    "payments.alreadySubscribed",
    "payments.alreadySubscribedOpeningPortal",
    "payments.alreadySubscribedPlanUpToDate",
  ];
  if (knownKeys.includes(message)) {
    const resolved = getTranslation(locale, message);
    if (resolved !== message) return resolved;
  }
  return message;
}

const toastStyles = {
  success: "bg-white border-neutral-200 text-neutral-900 shadow-sm",
  error: "bg-white border-red-200 text-neutral-900 shadow-sm",
  info: "bg-white border-neutral-200 text-neutral-900 shadow-sm",
  warning: "bg-white border-amber-200 text-neutral-900 shadow-sm",
};

// Add subtle left border accent for visual distinction
const accentStyles = {
  success: "border-l-4 border-l-green-500",
  error: "border-l-4 border-l-red-500",
  info: "border-l-4 border-l-blue-500",
  warning: "border-l-4 border-l-amber-500",
};

const iconStyles = {
  success: (
    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  ),
  error: (
    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
  ),
  info: (
    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  ),
  warning: (
    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    </div>
  ),
};

type ToastItemProps = {
  toast: Toast;
  onDismiss: (id: string) => void;
};

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const locale = useLocale();
  const displayMessage = resolveToastMessage(toast.message, locale);
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 200);
  };

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id]);

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border px-4 py-3 transition-all duration-200 ease-out w-full",
        toastStyles[toast.type],
        accentStyles[toast.type],
        isExiting ? "opacity-0 translate-x-full scale-95" : "opacity-100 translate-x-0 scale-100",
      )}
      role="alert"
      aria-live="polite"
    >
      {iconStyles[toast.type]}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-neutral-700 leading-snug">{displayMessage}</p>
        {toast.action && (
          <button
            onClick={() => {
              toast.action?.onClick();
              handleDismiss();
            }}
            className="mt-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-neutral-500 hover:text-neutral-600 transition-colors rounded hover:bg-neutral-100"
        aria-label="Dismiss notification"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);

  // Debug: Log toasts in production to help diagnose
  useEffect(() => {
    if (toasts.length > 0) {
      console.log('[ToastContainer] Toasts:', toasts.length, toasts);
    }
  }, [toasts]);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed left-0 right-0 flex flex-col gap-2 pointer-events-none px-4"
      style={{ 
        bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
        zIndex: 99999,
      }}
      aria-live="assertive"
      aria-atomic="true"
    >
      {toasts.map((toast: typeof toasts[number]) => (
        <div 
          key={toast.id} 
          className="w-full pointer-events-auto"
        >
          <ToastItem toast={toast} onDismiss={removeToast} />
        </div>
      ))}
    </div>
  );
}
